import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFirebase, isFirebaseConfigured } from "./app";

export type AuthListener = (user: User | null) => void;

const EMAIL_FOR_SIGN_IN_KEY = "dogdell-email-for-sign-in";
const PENDING_DOG_NAME_KEY = "dogdell-pending-dog-name";

export function watchAuth(listener: AuthListener): () => void {
  const fb = getFirebase();
  if (!fb) {
    listener(null);
    return () => undefined;
  }
  return onAuthStateChanged(fb.auth, listener);
}

export async function signInWithGoogle(): Promise<User> {
  const fb = requireFirebase();
  const result = await signInWithPopup(fb.auth, new GoogleAuthProvider());
  await ensurePlayerProfile(result.user);
  return result.user;
}

/** Email magic link — no password. Optionally stash a dog name for first-time saves. */
export async function sendMagicLink(email: string, pendingDogName?: string): Promise<void> {
  const fb = requireFirebase();
  const trimmed = email.trim();
  if (!trimmed) {
    throw new Error("Enter your email address.");
  }
  const url = `${window.location.origin}${window.location.pathname}`;
  await sendSignInLinkToEmail(fb.auth, trimmed, {
    url,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
  if (pendingDogName?.trim()) {
    window.localStorage.setItem(PENDING_DOG_NAME_KEY, pendingDogName.trim());
  } else {
    window.localStorage.removeItem(PENDING_DOG_NAME_KEY);
  }
}

/** Complete sign-in when the user returns from a magic link email. */
export async function completeMagicLinkSignInIfPresent(): Promise<User | null> {
  const fb = getFirebase();
  if (!fb || !isSignInWithEmailLink(fb.auth, window.location.href)) {
    return null;
  }
  let email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
  if (!email) {
    email = window.prompt("Confirm your email to finish signing in.")?.trim() ?? "";
  }
  if (!email) {
    throw new Error("Email is required to complete sign-in.");
  }
  const result = await signInWithEmailLink(fb.auth, email, window.location.href);
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  const pendingName = window.localStorage.getItem(PENDING_DOG_NAME_KEY);
  window.localStorage.removeItem(PENDING_DOG_NAME_KEY);
  if (pendingName && !result.user.displayName) {
    await updateProfile(result.user, { displayName: pendingName });
  }
  await ensurePlayerProfile(result.user, pendingName ?? undefined);
  window.history.replaceState({}, document.title, window.location.pathname);
  return result.user;
}

export async function signOutAccount(): Promise<void> {
  const fb = getFirebase();
  if (fb) {
    await signOut(fb.auth);
  }
}

export async function currentIdToken(): Promise<string | null> {
  const fb = getFirebase();
  const user = fb?.auth.currentUser;
  if (!user) {
    return null;
  }
  return user.getIdToken();
}

export function accountDogName(user: User, fallback: string): string {
  return (user.displayName || fallback).trim() || fallback;
}

async function ensurePlayerProfile(user: User, preferredName?: string): Promise<void> {
  const fb = getFirebase();
  if (!fb) {
    return;
  }
  const ref = doc(fb.db, "players", user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return;
  }
  await setDoc(ref, {
    uid: user.uid,
    name: preferredName || user.displayName || "Pup",
    email: user.email ?? null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
}

function requireFirebase() {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase is not configured for this build.");
  }
  const fb = getFirebase();
  if (!fb) {
    throw new Error("Firebase failed to initialize.");
  }
  return fb;
}

export { isFirebaseConfigured };
